/**
 * collabValidationHelper.js
 * Centralized navigation, focus, and error mapping for collaboration creation & edit forms.
 */

export const FIELD_ORDER_BY_STEP = {
  1: [
    'title',
    'sponsor_amount',
    'value',
    'quantity',
    'dates',
    'from_date',
    'to_date',
    'country',
    'description',
    'type',
  ],
  2: [
    'followers_requirements',
    'deliverables_mode',
    'exchange_options',
    'deliverables_counts',
    'target_options',
  ],
  3: [
    'featured_image',
    'additional_images',
    'terms_and_conditions',
    'sponsor_amount',
  ],
};

export const FIELD_LABEL_KEYS = {
  title: 'create.form.title_label',
  sponsor_amount: 'create.form.paid_amount_label',
  value: 'create.form.budget_label',
  quantity: 'create.form.creators_label',
  dates: 'create.form.dates_label',
  from_date: 'create.form.dates_start',
  to_date: 'create.form.dates_end',
  country: 'create.form.country_label',
  description: 'create.form.description_label',
  type: 'create.form.type_label',
  followers_requirements: 'create.form.followers_label',
  deliverables_mode: 'create.form.deliverables_mode_label',
  exchange_options: 'create.form.exchange_label',
  deliverables_counts: 'create.form.quantity_label',
  target_options: 'create.form.target_label',
  featured_image: 'create.form.featured_image_label',
  additional_images: 'create.form.additional_images_label',
  terms_and_conditions: 'create.form.terms_label',
};

export const getFieldSelector = (fieldKey, prefix = 'offer') => {
  const map = {
    title: `#${prefix}-title`,
    sponsor_amount: `#${prefix}-sponsor-amount, input[name="sponsor_amount"]`,
    value: `#${prefix}-value, #${prefix}-in-kind-value`,
    quantity: `#${prefix}-quantity`,
    dates: `#${prefix}-start-date`,
    from_date: `#${prefix}-start-date`,
    to_date: `#${prefix}-end-date`,
    country: `#${prefix}-country`,
    description: `#${prefix}-description-editor .ql-editor, #${prefix}-description-editor, [aria-labelledby="${prefix}-description-editor-label"]`,
    type: `input[name="collab-type"]`,
    followers_requirements: `input[name="collab-followers"]`,
    deliverables_mode: `input[name="collab-deliverables-mode"]`,
    exchange_options: `.v2-deliverables-list, .v2-deliverable-item button`,
    deliverables_counts: `.v2-deliverable-counter input, .v2-deliverables-list`,
    target_options: `.v2-pill-group button`,
    featured_image: `#${prefix}-featuredImageInput, #edit-featuredImageInput, #featuredImageInput, input[type="file"][accept*="image"]`,
    additional_images: `#${prefix}-additionalImagesInput, #edit-additionalImagesInput, #additionalImagesInput`,
    terms_and_conditions: `#${prefix}-terms-editor .ql-editor, #${prefix}-terms-editor, [aria-labelledby="${prefix}-terms-editor-label"]`,
  };
  return map[fieldKey] || `[name="${fieldKey}"], #${fieldKey}`;
};

/**
 * Returns the first field key in errors matching the step's visual order.
 */
export const getFirstErrorField = (errors = {}, step = 1) => {
  const order = FIELD_ORDER_BY_STEP[step] || [];
  for (const field of order) {
    if (errors[field]) return field;
  }
  // Fallback to any error key in errors
  const remainingKeys = Object.keys(errors).filter(k => Boolean(errors[k]));
  return remainingKeys[0] || null;
};

/**
 * Finds the step (1, 2, or 3) that contains the earliest error.
 */
export const findStepForError = (errors = {}) => {
  for (let s = 1; s <= 3; s++) {
    const firstInStep = getFirstErrorField(errors, s);
    if (firstInStep) return s;
  }
  return 1;
};

/**
 * Smoothly scrolls to the field and focuses its interactive control,
 * adding a subtle visual highlight pulse to guide the business user.
 */
export const scrollToAndFocusField = (fieldKey, prefix = 'offer', options = {}) => {
  const { navbarOffset = 96, pulse = true } = options;
  if (typeof document === 'undefined') return false;

  const selector = getFieldSelector(fieldKey, prefix);
  const element = document.querySelector(selector);
  if (!element) return false;

  const container = element.closest('.v2-form-group') || element.parentElement || element;

  try {
    const rect = element.getBoundingClientRect();
    const pageY = window.pageYOffset || document.documentElement.scrollTop || window.scrollY || 0;
    const targetTop = rect.top + pageY - navbarOffset;

    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: 'smooth',
    });
  } catch {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // Delay focus slightly so smooth scroll begins cleanly
  setTimeout(() => {
    try {
      if (typeof element.focus === 'function') {
        element.focus({ preventScroll: true });
      }
    } catch {
      // Ignore
    }

    if (pulse && container) {
      container.classList.remove('v2-field-pulse-highlight');
      // Force DOM reflow
      void container.offsetWidth;
      container.classList.add('v2-field-pulse-highlight');
      setTimeout(() => {
        container.classList.remove('v2-field-pulse-highlight');
      }, 2200);
    }
  }, 100);

  return true;
};

/**
 * Combined helper: finds first error in the current step (or across steps),
 * scrolls and focuses it. Returns the focused field key or null.
 */
export const scrollToAndFocusFirstError = (errors = {}, step = 1, prefix = 'offer', options = {}) => {
  const firstField = getFirstErrorField(errors, step);
  if (!firstField) return null;
  scrollToAndFocusField(firstField, prefix, options);
  return firstField;
};

/**
 * Parses DRF backend error payloads (e.g. { title: ["This field is required"] })
 * into a flat errors map { title: "This field is required" }.
 */
export const normalizeBackendErrors = (data) => {
  if (!data || typeof data !== 'object') return {};
  const normalized = {};

  Object.entries(data).forEach(([key, val]) => {
    if (Array.isArray(val)) {
      normalized[key] = val.join(' ');
    } else if (typeof val === 'string') {
      normalized[key] = val;
    } else if (typeof val === 'object' && val !== null) {
      normalized[key] = Object.values(val).flat().join(' ');
    }
  });

  return normalized;
};

/**
 * Validates that an image file is JPG or PNG.
 */
export const isImageFileValid = (file) => {
  if (!file) return false;
  const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
  const validExtensions = ['.jpg', '.jpeg', '.png'];
  const name = (file.name || '').toLowerCase();
  const hasValidExt = validExtensions.some(ext => name.endsWith(ext));
  const hasValidType = validTypes.includes(file.type);
  return hasValidExt || hasValidType;
};
